/**
 * DashboardTile - Renders a single dashboard tile with query results
 *
 * Supports multiple visualization types:
 * - Table view (using shared-ui DataTable)
 * - Bar chart (using shared-ui SimpleBarChart)
 * - Line chart (using recharts with shared-ui styling)
 */

import { Card, CardContent, CardHeader, CardTitle, SimpleBarChart } from "@getdevpulse/shared-ui"
import { Edit2, Trash2 } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useMemo } from "react"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import type { DashboardTile as DashboardTileType } from "../stores/CustomDashboardStore"

interface DashboardTileProps {
  tile: DashboardTileType
  onDelete?: () => void
  onEdit?: () => void
}

export const DashboardTile = observer(({ tile, onDelete, onEdit }: DashboardTileProps) => {
  const { title, queryResult, visualizationType, error } = tile

  // Transform data for charts
  const chartData = useMemo(() => {
    if (!queryResult || queryResult.rows.length === 0) return []

    // For charts, we need at least 2 columns (x-axis and y-axis)
    if (queryResult.columns.length < 2) return []

    return queryResult.rows.map((row) => {
      const item: Record<string, unknown> = {}
      for (const col of queryResult.columns) {
        item[col] = row[col]
      }
      return item
    })
  }, [queryResult])

  // Get numeric columns for chart y-axis
  const numericColumns = useMemo(() => {
    if (!queryResult || queryResult.rows.length === 0) return []

    return queryResult.columns.filter((col) => {
      const firstValue = queryResult.rows[0][col]
      return typeof firstValue === "number"
    })
  }, [queryResult])

  // Get first non-numeric column for x-axis (or first column if all numeric)
  const xAxisKey = useMemo(() => {
    if (!queryResult || queryResult.columns.length === 0) return ""

    const nonNumeric = queryResult.columns.find((col) => {
      const firstValue = queryResult.rows[0]?.[col]
      return typeof firstValue !== "number"
    })

    return nonNumeric || queryResult.columns[0]
  }, [queryResult])

  // Transform data for SimpleBarChart
  const simpleBarChartData = useMemo(() => {
    if (!queryResult || queryResult.rows.length === 0 || queryResult.columns.length < 2) return []

    const labelColumn = xAxisKey
    const valueColumn = numericColumns[0]

    if (!labelColumn || !valueColumn) return []

    return queryResult.rows.map((row) => ({
      label: String(row[labelColumn] || ""),
      value: Number(row[valueColumn]) || 0,
    }))
  }, [queryResult, xAxisKey, numericColumns])

  const renderContent = () => {
    if (error) {
      return (
        <div className="flex items-center justify-center h-full p-4">
          <div className="text-center">
            <p className="text-destructive text-sm mb-2">Error executing query</p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
        </div>
      )
    }

    if (!queryResult) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground text-sm">No data available</p>
        </div>
      )
    }

    if (queryResult.rows.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground text-sm">No results found</p>
        </div>
      )
    }

    switch (visualizationType) {
      case "table":
        return (
          <div className="overflow-auto h-full">
            <table className="min-w-full border-collapse">
              <thead className="sticky top-0 bg-muted">
                <tr>
                  {queryResult.columns.map((col) => (
                    <th
                      key={col}
                      className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider border-b"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {queryResult.rows.map((row, idx) => (
                  <tr
                    key={`${idx}-${JSON.stringify(row)}`}
                    className={idx % 2 === 0 ? "bg-background" : "bg-muted/50"}
                  >
                    {queryResult.columns.map((col) => (
                      <td key={col} className="px-3 py-2 text-sm border-b">
                        {String(row[col] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )

      case "bar":
        if (simpleBarChartData.length === 0 || numericColumns.length === 0) {
          return (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground text-sm">
                Chart requires at least one numeric column
              </p>
            </div>
          )
        }

        return (
          <div className="h-full overflow-auto p-4">
            <SimpleBarChart title="" data={simpleBarChartData} />
          </div>
        )

      case "line":
        if (chartData.length === 0 || numericColumns.length === 0) {
          return (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground text-sm">
                Chart requires at least one numeric column
              </p>
            </div>
          )
        }

        return (
          <div className="h-full overflow-auto p-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey={xAxisKey} />
                <YAxis />
                <Tooltip />
                <Legend />
                {numericColumns.map((col, idx) => (
                  <Line
                    key={col}
                    type="monotone"
                    dataKey={col}
                    stroke={`hsl(var(--chart-${(idx % 5) + 1}))`}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <Card className="h-full flex flex-col">
      {/* Header */}
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-semibold truncate">{title}</CardTitle>
        <div className="flex items-center gap-2">
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Edit tile"
            >
              <Edit2 className="h-4 w-4" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="text-muted-foreground hover:text-destructive transition-colors"
              title="Delete tile"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </CardHeader>

      {/* Content */}
      <CardContent className="flex-1 overflow-hidden p-0">{renderContent()}</CardContent>

      {/* Footer with metadata */}
      {tile.lastUpdated && (
        <div className="px-4 py-2 border-t text-xs text-muted-foreground">
          Last updated: {new Date(tile.lastUpdated).toLocaleString()}
        </div>
      )}
    </Card>
  )
})
